/**
 * Component for editing quiz settings including availability,
 * behavior, reporting, and approval options.
 *
 * @module components/quizzes/edit-quiz-reporting
 */

import type { Quiz } from '@efdigital/ll-addon-data';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import { type EditRecordSignature } from '../edit-record-base.gts';
import { EditRecordCollapse } from '../edit-record-collapse.gts';

export class EditQuizReporting extends Component<EditRecordSignature<Quiz>> {
  get reportAnswers(): boolean {
    return this.args.item.reportAnswers ?? false;
  }

  toggleReportAnswers = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.reportAnswers = target.checked;
  };

  get reportAnswersInternal(): boolean {
    return this.args.item.reportAnswersInternal ?? false;
  }

  toggleReportAnswersInternal = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.reportAnswersInternal = target.checked;
  };

  <template>
    <EditRecordCollapse
      @editLabel="Edit Reporting Settings"
      @item={{@item}}
      @context={{@context}}
      @maxDepth={{1}}
      @collapseLabel="Reporting"
      ...attributes
    >
      <:help>
        <p class="form-text">
          Control whether answers to this quiz can be displayed in the HR
          Dashboard.
        </p>
      </:help>
      <:form as |uniqueId isDisabled|>
        {{! Reporting Section }}
        <fieldset disabled={{isDisabled}} class="mb-0">
          <legend class="gud-eyebrow visually-hidden">Reporting</legend>

          <div class="form-check mb-3">
            <input
              type="checkbox"
              class="form-check-input"
              id="{{uniqueId}}-report-answers"
              checked={{this.reportAnswers}}
              {{on "change" this.toggleReportAnswers}}
            />
            <label class="form-check-label" for="{{uniqueId}}-report-answers">
              Include in dashboard reports
            </label>
            <div class="form-text">
              Export individual answers to client-facing dashboard reports.
            </div>
          </div>

          <div class="form-check">
            <input
              type="checkbox"
              class="form-check-input"
              id="{{uniqueId}}-report-internal"
              checked={{this.reportAnswersInternal}}
              {{on "change" this.toggleReportAnswersInternal}}
            />
            <label class="form-check-label" for="{{uniqueId}}-report-internal">
              Include in internal reports only
            </label>
            <div class="form-text">
              Export answers to internal operations reports only (not shared
              with clients).
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecordCollapse>
  </template>
}
